import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Info } from 'lucide-react';
import { Button } from '@/components/ui/button';

const AdvancedSettingsSection = ({ settings, setSettings }) => {
  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  return (
    <div className="space-y-8" data-testid="advanced-settings-section">
      {/* Price Execution Row */}
      <div className="grid md:grid-cols-2 gap-8">
        {/* Price Execution 1 */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <h4 className="text-base font-semibold text-gray-900">Price execution 1</h4>
            <Button variant="ghost" size="sm" className="h-auto p-0">
              <Info className="w-4 h-4 text-gray-400" />
            </Button>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-gray-600 mb-1 block">Initiation price</label>
              <Select
                value={settings.price_execution_1_initiation || 'Market Price'}
                onValueChange={(v) => updateSetting('price_execution_1_initiation', v)}
              >
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Market Price">Market Price</SelectItem>
                  <SelectItem value="Limit Price">Limit Price</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-gray-600 mb-1 block">Revision attempts</label>
              <Select
                value={String(settings.price_execution_1_revision || 1)}
                onValueChange={(v) => updateSetting('price_execution_1_revision', parseInt(v))}
              >
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1</SelectItem>
                  <SelectItem value="2">2</SelectItem>
                  <SelectItem value="3">3</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-gray-600 mb-1 block">Increase by</label>
              <Select
                value={settings.price_execution_1_increase_by || '1 Tick'}
                onValueChange={(v) => updateSetting('price_execution_1_increase_by', v)}
              >
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1 Tick">1 Tick</SelectItem>
                  <SelectItem value="2 Tick">2 Tick</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Price Execution 2 */}
        <div>
          <h4 className="text-base font-semibold text-gray-900 mb-4">Price execution 2</h4>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-gray-600 mb-1 block">Timeout</label>
              <Select
                value={settings.price_execution_2_timeout || '0 Second'}
                onValueChange={(v) => updateSetting('price_execution_2_timeout', v)}
              >
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0 Second">0 Second</SelectItem>
                  <SelectItem value="5 Second">5 Second</SelectItem>
                  <SelectItem value="10 Second">10 Second</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-gray-600 mb-1 block">Final action</label>
              <Select
                value={settings.price_execution_2_final_action || 'Execute at Ma'}
                onValueChange={(v) => updateSetting('price_execution_2_final_action', v)}
              >
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Execute at Ma">Execute at Ma</SelectItem>
                  <SelectItem value="Cancel">Cancel</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-gray-600 mb-1 block">Exit At Market Price</label>
              <Select
                value={settings.price_execution_2_exit_at_market || 'No - Follow Pri'}
                onValueChange={(v) => updateSetting('price_execution_2_exit_at_market', v)}
              >
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="No - Follow Pri">No - Follow Pri</SelectItem>
                  <SelectItem value="Yes">Yes</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>

      {/* Tranching and Trailing Stop Loss */}
      <div className="grid md:grid-cols-2 gap-8">
        {/* Tranching */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <h4 className="text-base font-semibold text-gray-900">Tranching</h4>
            <Button variant="ghost" size="sm" className="h-auto p-0">
              <Info className="w-4 h-4 text-gray-400" />
            </Button>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-gray-600 mb-1 block">Tranch size</label>
              <Select
                value={settings.tranching_size || '100%'}
                onValueChange={(v) => updateSetting('tranching_size', v)}
              >
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="100%">100%</SelectItem>
                  <SelectItem value="50%">50%</SelectItem>
                  <SelectItem value="25%">25%</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-gray-600 mb-1 block">One tranch every</label>
              <Select
                value={settings.tranching_frequency || '1 second'}
                onValueChange={(v) => updateSetting('tranching_frequency', v)}
              >
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1 second">1 second</SelectItem>
                  <SelectItem value="5 second">5 second</SelectItem>
                  <SelectItem value="10 second">10 second</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-gray-600 mb-1 block">Exit Shorts first</label>
              <Select
                value={settings.tranching_exit_shorts || 'No'}
                onValueChange={(v) => updateSetting('tranching_exit_shorts', v)}
              >
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="No">No</SelectItem>
                  <SelectItem value="Yes">Yes</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Trailing Stop Loss */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <h4 className="text-base font-semibold text-gray-900">Trailing Stop Loss</h4>
            <Button variant="ghost" size="sm" className="h-auto p-0">
              <Info className="w-4 h-4 text-gray-400" />
            </Button>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-gray-600 mb-1 block">Activate at</label>
              <Input
                type="number"
                value={settings.trailing_stop_loss_activate || 0}
                onChange={(e) => updateSetting('trailing_stop_loss_activate', parseInt(e.target.value) || 0)}
                className="h-9"
              />
            </div>
            <div>
              <label className="text-xs text-gray-600 mb-1 block">When profit increased by</label>
              <Input
                type="number"
                value={settings.trailing_stop_loss_profit_increase || 0}
                onChange={(e) => updateSetting('trailing_stop_loss_profit_increase', parseInt(e.target.value) || 0)}
                className="h-9"
              />
            </div>
            <div>
              <label className="text-xs text-gray-600 mb-1 block">Increase TSL by</label>
              <Input
                type="number"
                value={settings.trailing_stop_loss_increase_by || 0}
                onChange={(e) => updateSetting('trailing_stop_loss_increase_by', parseInt(e.target.value) || 0)}
                className="h-9"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Reactivate and Check Conditions */}
      <div className="grid md:grid-cols-2 gap-8">
        <div>
          <div className="flex items-center gap-2 mb-4">
            <h4 className="text-base font-semibold text-gray-900">Reactivate on exit after</h4>
            <Button variant="ghost" size="sm" className="h-auto p-0">
              <Info className="w-4 h-4 text-gray-400" />
            </Button>
          </div>
          <Select
            value={settings.reactivate_on_exit || 'Never'}
            onValueChange={(v) => updateSetting('reactivate_on_exit', v)}
            className="w-full"
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Never">Never</SelectItem>
              <SelectItem value="1 minute">1 minute</SelectItem>
              <SelectItem value="5 minutes">5 minutes</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <div className="flex items-center gap-2 mb-4">
            <h4 className="text-base font-semibold text-gray-900">Check conditions every</h4>
            <Button variant="ghost" size="sm" className="h-auto p-0">
              <Info className="w-4 h-4 text-gray-400" />
            </Button>
          </div>
          <Select
            value={settings.check_conditions_every || '1 minute'}
            onValueChange={(v) => updateSetting('check_conditions_every', v)}
            className="w-full"
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1 minute">1 minute</SelectItem>
              <SelectItem value="5 minutes">5 minutes</SelectItem>
              <SelectItem value="15 minutes">15 minutes</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Capital Required and Take Trades */}
      <div className="grid md:grid-cols-2 gap-8">
        <div>
          <h4 className="text-base font-semibold text-gray-900 mb-2">Capital Required</h4>
          <p className="text-xs text-gray-600 mb-2">User needed capital to deploy</p>
          <Input
            type="number"
            value={settings.capital_required || 100000}
            onChange={(e) => updateSetting('capital_required', parseInt(e.target.value) || 100000)}
          />
        </div>

        <div>
          <div className="flex items-center gap-2 mb-2">
            <h4 className="text-base font-semibold text-gray-900">Take trades</h4>
            <Button variant="ghost" size="sm" className="h-auto p-0">
              <Info className="w-4 h-4 text-gray-400" />
            </Button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-600 mb-1 block">Start Condition check after exchange open</label>
              <Select
                value={settings.take_trades_start || '10 minutes'}
                onValueChange={(v) => updateSetting('take_trades_start', v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10 minutes">10 minutes</SelectItem>
                  <SelectItem value="15 minutes">15 minutes</SelectItem>
                  <SelectItem value="30 minutes">30 minutes</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-gray-600 mb-1 block">Last Condition check before exchange close</label>
              <Select
                value={settings.take_trades_end || '10 minutes'}
                onValueChange={(v) => updateSetting('take_trades_end', v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10 minutes">10 minutes</SelectItem>
                  <SelectItem value="15 minutes">15 minutes</SelectItem>
                  <SelectItem value="30 minutes">30 minutes</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>

      {/* Rollover on Expiry and Python Code */}
      <div className="grid md:grid-cols-2 gap-8">
        <div>
          <div className="flex items-center gap-2 mb-4">
            <h4 className="text-base font-semibold text-gray-900">Rollover on expiry</h4>
            <Button variant="ghost" size="sm" className="h-auto p-0">
              <Info className="w-4 h-4 text-gray-400" />
            </Button>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-gray-600 mb-1 block">Choose expiry</label>
              <Select
                value={settings.rollover_expiry || 'Dont Rollover'}
                onValueChange={(v) => updateSetting('rollover_expiry', v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Dont Rollover">Dont Rollover</SelectItem>
                  <SelectItem value="Weekly">Weekly</SelectItem>
                  <SelectItem value="Monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-gray-600 mb-1 block">Hours</label>
              <Select
                value={String(settings.rollover_hours || 15)}
                onValueChange={(v) => updateSetting('rollover_hours', parseInt(v))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="15">15</SelectItem>
                  <SelectItem value="14">14</SelectItem>
                  <SelectItem value="13">13</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-gray-600 mb-1 block">Minutes</label>
              <Select
                value={String(settings.rollover_minutes || 0)}
                onValueChange={(v) => updateSetting('rollover_minutes', parseInt(v))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="00">00</SelectItem>
                  <SelectItem value="15">15</SelectItem>
                  <SelectItem value="30">30</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <div>
          <div className="flex items-center gap-2 mb-4">
            <h4 className="text-base font-semibold text-gray-900">Python code</h4>
            <Button variant="ghost" size="sm" className="h-auto p-0">
              <Info className="w-4 h-4 text-gray-400" />
            </Button>
          </div>
          <Textarea
            value={settings.python_code || ''}
            onChange={(e) => updateSetting('python_code', e.target.value)}
            placeholder="# Enter custom Python code here"
            rows={4}
            className="font-mono text-sm"
          />
        </div>
      </div>
    </div>
  );
};

export default AdvancedSettingsSection;
